import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumn, removeColumn } from '../utils/schemas'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class RenameColumn extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.renamecolumn',
        group: 'Data Preparation',
        title: 'Rename Column',
        description: `
      This component generates a new table with the same content as the input one, renaming
      one of its columns.
      `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'column',
          type: WorkflowComponentParamType.Column,
          title: 'Column to rename',
          parent: 'source'
        },
        {
          name: 'newname',
          type: WorkflowComponentParamType.String,
          title: 'New column name',
          validation: '^[a-zA-Z_][a-zA-Z0-9_]*$',
          allowExpressions: false
        }
      ]
    )
  }

  getVersion(provider: Provider) {
    return '2'
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)
    let query = ''
    const newname = `${preparedParams.newname}`.replace(/[()]/g, '') // remove parentheses if expression
    switch (connection.type) {
      case Provider.BigQuery: {
        const select = `SELECT * EXCEPT (${preparedParams.column}),
                        ${preparedParams.column} AS ${newname}
                      FROM ${connection.quoteName(preparedParams.source as string)}`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
      case Provider.DatabricksRest: {
        const select = `SELECT /*+ REPARTITION(40) */
                        * EXCEPT (${preparedParams.column}),
                        ${preparedParams.column} AS ${newname}
                      FROM ${connection.quoteName(preparedParams.source as string)}`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
      case Provider.Snowflake: {
        const select = `SELECT * RENAME (
                        ${preparedParams.column}
                        AS ${newname}
                      )
                      FROM ${connection.quoteName(preparedParams.source as string)}`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
      case Provider.Postgres: {
        const select = `SELECT *
                        FROM ${connection.quoteName(preparedParams.source as string)}`
        query += connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        query += `
        ALTER TABLE ${outputTablename}
        RENAME COLUMN ${preparedParams.column}
        TO ${newname};`
        break
      }
      default: {
        const select = `SELECT *
                        FROM ${connection.quoteName(preparedParams.source as string)}`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        query += `
        ALTER TABLE ${outputTablename}
        RENAME COLUMN ${preparedParams.column}
        TO ${newname};`
        break
      }
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    return addColumn(removeColumn(columns.source, params.column as string), {
      name: params.newname as string,
      type: columns.source.find((c) => c.name === params.column)?.type as ColumnDataType
    })
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Postgres, Provider.Redshift, Provider.Snowflake, Provider.DatabricksRest]
  }
}

export { RenameColumn }
